import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil, Printer, Trash2 } from 'lucide-react';
import { useTicket, useAddTicketComment, useDeleteTicket } from '../hooks/useApi';
import TicketModal from '../components/tickets/TicketModal';
import {
    Avatar,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    ConfirmDialog,
    DetailRow,
    EmptyState,
    ErrorState,
    ListSkeleton,
    PageHeader,
    PriorityBadge,
    Skeleton,
    StatusBadge,
} from '../components/ui';

const TicketDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: ticket, isLoading, isError } = useTicket(id);
    const addComment = useAddTicketComment();
    const deleteTicket = useDeleteTicket();

    const [commentText, setCommentText] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!commentText.trim()) return;
        try {
            await addComment.mutateAsync({ id, comment: commentText });
            setCommentText('');
        } catch (error) {
            console.error('Comment failed', error);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteTicket.mutateAsync(id);
            navigate('/tickets');
        } catch (error) {
            console.error('Delete failed', error);
        }
    };

    if (isLoading) {
        return (
            <>
                <Skeleton w={260} h={26} className="mb-5" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Card className="lg:col-span-2">
                        <CardBody>
                            <ListSkeleton rows={5} />
                        </CardBody>
                    </Card>
                    <Card>
                        <CardBody>
                            <ListSkeleton rows={3} />
                        </CardBody>
                    </Card>
                </div>
            </>
        );
    }

    if (isError || !ticket) {
        return (
            <Card>
                <ErrorState
                    title="Ticket not found"
                    description="It may have been deleted, or you may not have access to it."
                    onRetry={() => navigate('/tickets')}
                />
            </Card>
        );
    }

    const comments = ticket.comments || [];

    return (
        <>
            <PageHeader
                title={ticket.title}
                breadcrumbs={[
                    { label: 'Tickets', to: '/tickets' },
                    { label: ticket.ticket_code || 'Ticket' },
                ]}
                actions={
                    <>
                        <Button icon={Pencil} onClick={() => setIsEditModalOpen(true)}>
                            Edit
                        </Button>
                        <Button variant="danger" icon={Trash2} onClick={() => setIsDeleteModalOpen(true)}>
                            Delete
                        </Button>
                    </>
                }
            >
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <StatusBadge status={ticket.status} />
                    <PriorityBadge priority={ticket.priority} />
                    <span className="text-label font-mono text-ink-500">{ticket.ticket_code}</span>
                    <span className="text-ink-300" aria-hidden="true">·</span>
                    <span className="text-label text-ink-600">
                        Raised by {ticket.created_by_name} on{' '}
                        {new Date(ticket.created_at).toLocaleDateString(undefined, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                        })}
                    </span>
                </div>
            </PageHeader>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2 space-y-4">
                    <Card>
                        <CardHeader title="Description" />
                        <CardBody>
                            {ticket.description ? (
                                <p className="text-md text-ink-700 whitespace-pre-wrap leading-relaxed max-w-[75ch]">
                                    {ticket.description}
                                </p>
                            ) : (
                                <p className="text-base text-ink-500">No description provided.</p>
                            )}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHeader
                            title="Replies"
                            subtitle={comments.length ? `${comments.length} messages` : undefined}
                        />
                        <CardBody>
                            <form onSubmit={handleAddComment} className="mb-5">
                                <label htmlFor="ticket-comment" className="sr-only">
                                    Add a reply
                                </label>
                                <textarea
                                    id="ticket-comment"
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    className="input textarea"
                                    rows={3}
                                    placeholder="Type your reply…"
                                    required
                                />
                                <div className="flex justify-end mt-2">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        size="sm"
                                        loading={addComment.isPending}
                                    >
                                        Send reply
                                    </Button>
                                </div>
                            </form>

                            {comments.length ? (
                                <ol className="space-y-4">
                                    {comments.map((comment) => (
                                        <li key={comment.id} className="flex gap-2.5">
                                            <Avatar name={comment.user_name} size="md" />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-baseline gap-2 flex-wrap">
                                                    <span className="text-base font-medium text-ink-900">
                                                        {comment.user_name}
                                                    </span>
                                                    <span className="text-micro text-ink-400 tabular">
                                                        {new Date(comment.created_at).toLocaleString()}
                                                    </span>
                                                </div>
                                                <p className="text-base text-ink-700 whitespace-pre-wrap mt-0.5 leading-relaxed">
                                                    {comment.comment}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <EmptyState
                                    compact
                                    title="No replies yet"
                                    description="Keep all correspondence in this thread so it stays auditable."
                                />
                            )}
                        </CardBody>
                    </Card>
                </div>

                <div className="space-y-4">
                    <Card>
                        <CardHeader title="Ticket details" />
                        <CardBody className="divide-y divide-[var(--divider-color)] py-2">
                            <DetailRow label="Status">
                                <StatusBadge status={ticket.status} />
                            </DetailRow>
                            <DetailRow label="Priority">
                                <PriorityBadge priority={ticket.priority} />
                            </DetailRow>
                            <DetailRow label="Category">
                                <Badge variant="neutral">{ticket.category || 'Support'}</Badge>
                            </DetailRow>
                            <DetailRow label="Assignee">
                                {ticket.assignee_name ? (
                                    <span className="flex items-center gap-2">
                                        <Avatar name={ticket.assignee_name} size="sm" />
                                        {ticket.assignee_name}
                                    </span>
                                ) : (
                                    <span className="text-muted">Unassigned</span>
                                )}
                            </DetailRow>
                            <DetailRow label="Raised by">{ticket.created_by_name}</DetailRow>
                            {ticket.resolved_at && (
                                <DetailRow label="Resolved">
                                    <span className="tabular text-success-strong">
                                        {new Date(ticket.resolved_at).toLocaleDateString(undefined, {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                        })}
                                    </span>
                                </DetailRow>
                            )}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody>
                            <p className="text-base text-ink-600 leading-relaxed">
                                Tickets are handled by the support team. Keep all correspondence in this
                                thread so the record stays complete.
                            </p>
                            <Button
                                icon={Printer}
                                block
                                className="mt-3 no-print"
                                onClick={() => window.print()}
                            >
                                Print this ticket
                            </Button>
                        </CardBody>
                    </Card>
                </div>
            </div>

            <TicketModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                ticket={ticket}
            />

            <ConfirmDialog
                isOpen={isDeleteModalOpen}
                title="Delete ticket"
                message="This permanently removes the ticket and its entire reply history. This cannot be undone."
                confirmText="Delete ticket"
                variant="danger"
                isLoading={deleteTicket.isPending}
                onConfirm={handleDelete}
                onClose={() => setIsDeleteModalOpen(false)}
            />
        </>
    );
};

export default TicketDetail;
